import React, { useState, useEffect, useCallback } from 'react';
import { Search, Eye, Calendar, Phone, X, Loader, CheckCircle, Trash2, Clock, Image as ImageIcon } from 'lucide-react';
import api from '../../utils/api';

const SC = {
  viewed:    'bg-gray-100 text-gray-600',
  new:       'bg-amber-100 text-amber-800',
  converted: 'bg-green-100 text-green-800',
};

const DATE_TABS = [
  { id: 'today',      label: 'Today'      },
  { id: 'yesterday',  label: 'Yesterday'  },
  { id: 'this_week',  label: 'This Week'  },
  { id: 'last_week',  label: 'Last Week'  },
  { id: 'this_month', label: 'This Month' },
  { id: '',           label: 'All Time'   },
];

// Brisbane is always UTC+10 (no daylight saving in QLD)
function toBrisbaneDate(utcStr) {
  if (!utcStr) return '';
  const d = new Date(utcStr);
  const bris = new Date(d.getTime() + 10 * 60 * 60 * 1000);
  return bris.toISOString().split('T')[0];
}
function toBrisbaneTime(utcStr) {
  if (!utcStr) return '';
  const d = new Date(utcStr);
  const bris = new Date(d.getTime() + 10 * 60 * 60 * 1000);
  return bris.toISOString().split('T')[1]?.slice(0,5);
}

function getTabFilter(tab) {
  // All dates in Brisbane time (UTC+10)
  const nowBris = new Date(Date.now() + 10 * 60 * 60 * 1000);
  const todayStr = nowBris.toISOString().split('T')[0];

  const yday = new Date(nowBris); yday.setUTCDate(yday.getUTCDate() - 1);
  const ydayStr = yday.toISOString().split('T')[0];

  const weekStart = new Date(nowBris);
  weekStart.setUTCDate(weekStart.getUTCDate() - weekStart.getUTCDay());
  const weekStartStr = weekStart.toISOString().split('T')[0];

  const lastWeekEnd = new Date(weekStart); lastWeekEnd.setUTCDate(lastWeekEnd.getUTCDate() - 1);
  const lastWeekStart = new Date(lastWeekEnd); lastWeekStart.setUTCDate(lastWeekStart.getUTCDate() - 6);

  const monthStart = new Date(nowBris.getUTCFullYear(), nowBris.getUTCMonth(), 1);
  const monthStartStr = monthStart.toISOString().split('T')[0];

  return (e) => {
    if (!tab) return true;
    const d = toBrisbaneDate(e.created_at); // convert UTC to Brisbane date
    if (!d) return false;
    switch (tab) {
      case 'today':      return d === todayStr;
      case 'yesterday':  return d === ydayStr;
      case 'this_week':  return d >= weekStartStr  && d <= todayStr;
      case 'last_week':  return d >= lastWeekStart.toISOString().split('T')[0] && d <= lastWeekEnd.toISOString().split('T')[0];
      case 'this_month': return d >= monthStartStr && d <= todayStr;
      default:           return true;
    }
  };
}

function TimePicker({ value, onChange }) {
  const parse = v => {
    if (!v) return { h:'09', m:'00', p:'AM' };
    const [hh, mm] = v.split(':');
    const h24 = parseInt(hh);
    return { h: String(h24===0?12:h24>12?h24-12:h24).padStart(2,'0'), m: mm||'00', p: h24>=12?'PM':'AM' };
  };
  const fmt = (h, m, p) => {
    let h24 = parseInt(h);
    if (p==='PM' && h24!==12) h24 += 12;
    if (p==='AM' && h24===12) h24 = 0;
    return `${String(h24).padStart(2,'0')}:${m}`;
  };
  const { h, m, p } = parse(value);
  const cls = "px-2 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500";
  return (
    <div className="flex gap-2 items-center">
      <select value={h} onChange={e => onChange(fmt(e.target.value, m, p))} className={`${cls} flex-1`}>
        {['06','07','08','09','10','11','12','01','02','03','04','05'].map(v => <option key={v}>{v}</option>)}
      </select>
      <span className="font-bold text-gray-400">:</span>
      <select value={m} onChange={e => onChange(fmt(h, e.target.value, p))} className={`${cls} flex-1`}>
        {['00','15','30','45'].map(v => <option key={v}>{v}</option>)}
      </select>
      <div className="flex border border-gray-200 rounded-xl overflow-hidden shrink-0">
        {['AM','PM'].map(period => (
          <button key={period} type="button" onClick={() => onChange(fmt(h, m, period))}
            className={`px-3 py-2 text-xs font-bold ${p===period ? 'bg-apex-blue-700 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
            {period}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Estimates() {
  const [estimates, setEstimates]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [dateTab, setDateTab]         = useState('today');
  const [selected, setSelected]       = useState(null);
  const [scheduling, setScheduling]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast]             = useState('');
  const [schedSaving, setSchedSaving] = useState(false);
  const [imageLightbox, setImageLightbox] = useState(null); // {images:[], idx:0}
  const [schedForm, setSchedForm]     = useState({
    date: '', time: '09:00', price: '',
    vehicle_make: '', vehicle_model: '', vehicle_year: '',
    vehicle_rego: '', vehicle_condition: '', notes: '',
  });

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/estimates');
      setEstimates(res.data);
    } catch (e) {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tabFilter   = getTabFilter(dateTab);
  const tabCount    = (id) => estimates.filter(getTabFilter(id)).length;
  const newCount    = estimates.filter(e => !e.viewed && !e.converted).length;

  const filtered = estimates.filter(e => {
    if (!tabFilter(e)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [e.customer_name, e.customer_phone, e.pickup, e.dropoff, e.service_type]
      .some(f => (f||'').toLowerCase().includes(q));
  });

  const view = async (e) => {
    setSelected(e);
    if (!e.viewed && !e.converted) {
      await api.put(`/api/estimates/${e.id}`, { viewed: true });
      load();
    }
  };

  const openSchedule = (est) => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    setSchedForm({
      date: d.toISOString().split('T')[0],
      time: '09:00',
      price: String(est.est_min || ''),
      vehicle_make: '', vehicle_model: '', vehicle_year: '',
      vehicle_rego: '', vehicle_condition: '', notes: '',
    });
    setScheduling(est);
    setSelected(null);
  };

  const confirmSchedule = async () => {
    if (!schedForm.date || !scheduling) return;
    setSchedSaving(true);
    try {
      await api.post('/api/bookings', {
        customer_name:    scheduling.customer_name,
        customer_phone:   scheduling.customer_phone,
        customer_email:   scheduling.customer_email,
        service_type:     scheduling.service_type || 'Standard Towing',
        pickup_address:   scheduling.pickup,
        pickup_suburb:    scheduling.pickup,
        dropoff_address:  scheduling.dropoff,
        dropoff_suburb:   scheduling.dropoff,
        scheduled_date:   schedForm.date,
        scheduled_time:   schedForm.time,
        charge:           Number(schedForm.price) || 0,
        vehicle_make:     schedForm.vehicle_make,
        vehicle_model:    schedForm.vehicle_model,
        vehicle_year:     schedForm.vehicle_year,
        vehicle_rego:     schedForm.vehicle_rego,
        vehicle_condition: schedForm.vehicle_condition,
        notes:            schedForm.notes,
        booking_type:     'customer',
      });
      await api.put(`/api/estimates/${scheduling.id}`, { converted: true });
      showToast('✅ Booking created successfully!');
      setScheduling(null);
      load();
    } catch { alert('Failed to create booking.'); }
    finally { setSchedSaving(false); }
  };

  const deleteEstimate = async (id) => {
    try {
      await api.delete(`/api/estimates/${id}`);
      showToast('Estimate deleted');
      setDeleteTarget(null);
      setSelected(null);
      load();
    } catch { alert('Delete failed — make sure backend is running.'); }
  };

  const getStatus = (e) => e.converted ? 'converted' : e.viewed ? 'viewed' : 'new';

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-apex-blue-700 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2 animate-bounce">
          <CheckCircle className="w-4 h-4" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">Estimates</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {filtered.length} shown
            {newCount > 0 && <span className="text-red-500 font-bold ml-2">· {newCount} new</span>}
          </p>
        </div>
      </div>

      {/* Date tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {DATE_TABS.map(({ id, label }) => {
          const cnt = tabCount(id);
          return (
            <button key={id} onClick={() => setDateTab(id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border-2 whitespace-nowrap shrink-0 transition-all
                ${dateTab===id ? 'bg-apex-blue-700 border-apex-blue-700 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
              {label}
              {cnt > 0 && (
                <span className={`text-[11px] font-black px-1.5 py-0.5 rounded-full min-w-[20px] text-center leading-none
                  ${dateTab===id ? 'bg-white/25 text-white' : 'bg-apex-blue-100 text-apex-blue-700'}`}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 mb-4 max-w-lg">
        <Search className="w-4 h-4 text-gray-300 shrink-0" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, phone, suburb, service..." className="outline-none text-sm flex-1" />
        {search && <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500 text-xs">✕</button>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader className="w-8 h-8 text-apex-blue-500 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[750px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {['Customer','Contact','Route','Vehicle / Service','Estimate','Submitted','Status','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-16">
                    <Clock className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No estimates for this period</p>
                  </td></tr>
                )}
                {filtered.map(e => {
                  const st = getStatus(e);
                  return (
                    <tr key={e.id} onClick={() => view(e)}
                      className={`cursor-pointer hover:bg-blue-50/50 transition-colors ${st==='new' ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-4 py-3.5">
                        <p className="font-bold text-gray-900">{e.customer_name}</p>
                        {st==='new' && <span className="text-[10px] bg-red-500 text-white font-black px-2 py-0.5 rounded-full">NEW</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-semibold text-gray-800">{e.customer_phone}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{e.customer_email || '—'}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-800">{e.pickup || '—'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">→ {e.dropoff || 'N/A'}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-800 capitalize">{e.vehicle_type || '—'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{e.service_type || ''}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="font-black text-apex-blue-700 text-base whitespace-nowrap">
                          ${(e.est_min||0).toLocaleString()} – ${(e.est_max||0).toLocaleString()}
                        </p>
                        {e.distance_km > 0 && <p className="text-xs text-gray-400">{e.distance_km} km</p>}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <p className="font-semibold text-gray-800">{toBrisbaneDate(e.created_at)}</p>
                        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />{toBrisbaneTime(e.created_at)}
                        </p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full capitalize ${SC[st]||'bg-gray-100 text-gray-600'}`}>{st}</span>
                      </td>
                      <td className="px-4 py-3.5" onClick={f => f.stopPropagation()}>
                        <div className="flex gap-1.5">
                          <button onClick={() => view(e)} title="View Details"
                            className="p-2 rounded-xl border border-gray-200 hover:bg-blue-50 text-apex-blue-500" >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {/* Image view button — only show if customer uploaded photos */}
                          {e.images?.length > 0 && (
                            <button onClick={() => setImageLightbox({ images: e.images, idx: 0 })} title={`View ${e.images.length} photo(s)`}
                              className="p-2 rounded-xl border border-purple-200 hover:bg-purple-50 text-purple-600 relative">
                              <ImageIcon className="w-3.5 h-3.5" />
                              <span className="absolute -top-1 -right-1 bg-purple-600 text-white text-[8px] font-black rounded-full w-3.5 h-3.5 flex items-center justify-center">
                                {e.images.length}
                              </span>
                            </button>
                          )}
                          {!e.converted && (
                            <button onClick={() => openSchedule(e)} title="Schedule & Book"
                              className="p-2 rounded-xl border border-green-200 hover:bg-green-50 text-green-600">
                              <Calendar className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <a href={`tel:${e.customer_phone}`} title="Call Customer"
                            className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600">
                            <Phone className="w-3.5 h-3.5" />
                          </a>
                          <button onClick={() => setDeleteTarget(e.id)} title="Delete"
                            className="p-2 rounded-xl border border-red-200 hover:bg-red-50 text-red-500">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── View Details Modal ── */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => { if(e.target===e.currentTarget) setSelected(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-apex-blue-700 px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Estimate Details</p>
                  <p className="text-white font-black text-xl mt-0.5">{selected.customer_name}</p>
                  <p className="text-white/50 text-xs mt-0.5">{selected.customer_phone} · {selected.customer_email || 'No email'}</p>
                </div>
                <button onClick={() => setSelected(null)} className="bg-white/15 text-white p-2 rounded-lg">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Service',   selected.service_type || '—'],
                  ['Vehicle',   selected.vehicle_type || '—'],
                  ['From',      selected.pickup || '—'],
                  ['To',        selected.dropoff || '—'],
                  ['Distance',  selected.distance_km ? `${selected.distance_km} km` : '—'],
                  ['Condition', selected.condition || '—'],
                  ['Submitted', selected.created_at?.split('T')[0]],
                  ['Time',      selected.created_at?.split('T')[1]?.slice(0,5)],
                ].map(([l,v]) => (
                  <div key={l}>
                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-0.5">{l}</p>
                    <p className="text-sm font-semibold text-gray-800">{v}</p>
                  </div>
                ))}
              </div>

              <div className="bg-apex-blue-700 rounded-xl p-5 text-center">
                <p className="text-xs text-white/60 font-bold uppercase tracking-wide mb-1">Estimated Cost</p>
                <p className="text-4xl font-black text-apex-yellow-400">
                  ${(selected.est_min||0).toLocaleString()} – ${(selected.est_max||0).toLocaleString()}
                </p>
                <p className="text-xs text-white/40 mt-1">incl. GST · final price confirmed on job</p>
              </div>

              {/* Customer Photos */}
              {selected.images?.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" /> Customer Photos ({selected.images.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.images.map((img, i) => (
                      <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
                        onClick={() => setImageLightbox({ images: selected.images, idx: i })}>
                        <img src={img} alt="" className="w-full h-full object-cover hover:opacity-90 transition-opacity" />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black/30 transition-opacity">
                          <span className="text-white text-xs font-bold">View</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {!selected.converted ? (
                  <button onClick={() => openSchedule(selected)}
                    className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 flex items-center justify-center gap-2">
                    <Calendar className="w-4 h-4" /> Schedule & Book
                  </button>
                ) : (
                  <div className="flex-1 bg-green-50 border-2 border-green-200 text-green-700 py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-sm">
                    ✓ Already Booked
                  </div>
                )}
                <a href={`tel:${selected.customer_phone}`}
                  className="flex-1 bg-apex-blue-700 text-white py-3 rounded-xl font-bold text-center hover:bg-apex-blue-600 flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" /> Call Now
                </a>
              </div>
              <button onClick={() => setDeleteTarget(selected.id)}
                className="w-full py-2.5 border-2 border-red-200 text-red-500 rounded-xl font-bold hover:bg-red-50 flex items-center justify-center gap-2 text-sm">
                <Trash2 className="w-4 h-4" /> Delete Estimate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Schedule & Book Modal ── */}
      {scheduling && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl my-4">
            <div className="bg-apex-blue-700 rounded-t-2xl px-6 py-5">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">Schedule & Book</p>
                  <p className="text-white font-black text-lg mt-0.5">{scheduling.customer_name}</p>
                  <p className="text-white/50 text-xs mt-0.5">{scheduling.service_type} · {scheduling.pickup} → {scheduling.dropoff}</p>
                </div>
                <button onClick={() => setScheduling(null)} className="bg-white/15 text-white p-2 rounded-lg"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Date & Time */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-xs font-black text-gray-500 uppercase tracking-wide">📅 When to Schedule</p>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Date *</label>
                  <input type="date" value={schedForm.date}
                    onChange={e => setSchedForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500" />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Time</label>
                  <TimePicker value={schedForm.time} onChange={v => setSchedForm(f => ({ ...f, time: v }))} />
                </div>
              </div>

              {/* Confirmed Price */}
              <div className="bg-apex-blue-50 border-2 border-apex-blue-100 rounded-xl p-4">
                <p className="text-xs font-black text-apex-blue-700 uppercase tracking-wide mb-2">💰 Confirm Final Price</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black text-gray-400">$</span>
                  <input type="number" value={schedForm.price}
                    onChange={e => setSchedForm(f => ({ ...f, price: e.target.value }))}
                    className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-lg font-black focus:outline-none focus:border-apex-blue-500"
                    placeholder={String(scheduling.est_min||'')} />
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Estimate was ${(scheduling.est_min||0).toLocaleString()} – ${(scheduling.est_max||0).toLocaleString()}
                </p>
              </div>

              {/* Optional Vehicle */}
              <div>
                <p className="text-xs font-black text-gray-500 uppercase tracking-wide mb-2">🚗 Vehicle Details (optional)</p>
                <div className="grid grid-cols-3 gap-2">
                  {[['Make','vehicle_make','Toyota'],['Model','vehicle_model','Corolla'],['Year','vehicle_year','2020']].map(([l,k,ph]) => (
                    <div key={k}>
                      <label className="text-xs font-bold text-gray-400 block mb-1">{l}</label>
                      <input value={schedForm[k]} onChange={e => setSchedForm(f => ({ ...f, [k]: e.target.value }))}
                        placeholder={ph} className="w-full px-2 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className="text-xs font-bold text-gray-400 block mb-1">Rego</label>
                    <input value={schedForm.vehicle_rego} onChange={e => setSchedForm(f => ({ ...f, vehicle_rego: e.target.value }))}
                      className="w-full px-2 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-400 block mb-1">Condition</label>
                    <select value={schedForm.vehicle_condition} onChange={e => setSchedForm(f => ({ ...f, vehicle_condition: e.target.value }))}
                      className="w-full px-2 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none">
                      <option value="">Select</option>
                      <option value="running">Running</option>
                      <option value="not_running">Not Running</option>
                      <option value="accident_damaged">Accident Damaged</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Notes</label>
                <textarea value={schedForm.notes} onChange={e => setSchedForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none" rows={2}
                  placeholder="Special instructions, access notes..." />
              </div>

              <button onClick={confirmSchedule} disabled={!schedForm.date || schedSaving}
                className="w-full py-4 bg-green-600 text-white font-black rounded-xl hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 text-base">
                {schedSaving ? <Loader className="w-5 h-5 animate-spin" /> : <><Calendar className="w-5 h-5" /> Confirm Booking</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="font-black text-gray-900 mb-2">Delete This Estimate?</h3>
            <p className="text-sm text-gray-500 mb-5">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 border-2 border-gray-200 py-2.5 rounded-xl font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => deleteEstimate(deleteTarget)} className="flex-1 bg-red-500 text-white py-2.5 rounded-xl font-bold hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {imageLightbox && (
        <div className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
          onClick={() => setImageLightbox(null)}>
          <div className="relative max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            {/* Close */}
            <button onClick={() => setImageLightbox(null)}
              className="absolute -top-10 right-0 text-white/60 hover:text-white font-bold text-sm flex items-center gap-1.5">
              <X className="w-4 h-4" /> Close
            </button>
            {/* Image */}
            <img src={imageLightbox.images[imageLightbox.idx]} alt=""
              className="w-full max-h-[70vh] object-contain rounded-xl" />
            {/* Counter */}
            <p className="text-white/50 text-xs text-center mt-3">
              Photo {imageLightbox.idx + 1} of {imageLightbox.images.length} · Customer uploaded
            </p>
            {/* Prev/Next */}
            {imageLightbox.images.length > 1 && (
              <div className="flex justify-center gap-3 mt-3">
                <button onClick={() => setImageLightbox(l => ({ ...l, idx: Math.max(0, l.idx-1) }))}
                  disabled={imageLightbox.idx === 0}
                  className="bg-white/10 text-white px-4 py-2 rounded-xl font-bold text-sm disabled:opacity-30 hover:bg-white/20">
                  ← Prev
                </button>
                <button onClick={() => setImageLightbox(l => ({ ...l, idx: Math.min(l.images.length-1, l.idx+1) }))}
                  disabled={imageLightbox.idx === imageLightbox.images.length-1}
                  className="bg-white/10 text-white px-4 py-2 rounded-xl font-bold text-sm disabled:opacity-30 hover:bg-white/20">
                  Next →
                </button>
              </div>
            )}
            {/* Thumbnails */}
            {imageLightbox.images.length > 1 && (
              <div className="flex gap-2 justify-center mt-3 flex-wrap">
                {imageLightbox.images.map((img, i) => (
                  <img key={i} src={img} alt="" onClick={() => setImageLightbox(l => ({ ...l, idx: i }))}
                    className={`w-14 h-10 object-cover rounded-lg cursor-pointer border-2 transition-all
                      ${i === imageLightbox.idx ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}